
import { Vue, Component, Watch } from 'vue-property-decorator';
import { logout, canEdit, isOperatorOfAgent } from '@/utils/authUtil';
import { State, Getter, Action, Mutation } from 'vuex-class';
import BytedIconRight from '@byte-design/vue-icons/icons/Right';
import {
  getAccountStatusI18n,
  getAccountStatusColor,
  ifPaymentDisable,
  getAccountStatusItem,
} from '@/const/state/account';
import qs from 'qs';
import { TeaConfigPayment, CurrentBillStatus } from '@/const/payment';
import Popup from '@/components/popup/popup.vue';
import AccountList from '@/components/account-list.vue';
import { TeaConfig } from '@/const/dashboard';
import Highlight from '@/components/highlight.vue';
import Balance from '@/components/balance.vue';
import apis from '@/const/apis';
import axios from '@/utils/axios';
import { SET_CREDIT_INFO, SET_ENTER_ACCOUNT_INFO } from '@/store/types';
import forTestMixin from '@/mixins/forTestMixin';
import { reportXFmp } from '@/plugin/perfMeasurePlugin';
import { getRiskInfo } from '@/utils/tool';
import { getAbVersionByName, LibraTestName, LibraTestAbVersionResult } from '@/AbtestVersion';
import { domainTTP, PAYMENT_SCENE } from '@/const/common';

const namespace = 'account';

@Component({
  name: 'Setting',
  mixins: [forTestMixin],
  waitApiList: [apis.account.balance, apis.payment.charge_info, apis.payment.getBindLink],
  components: {
    BytedIconRight,
    Popup,
    AccountList,
    Highlight,
    Balance,
  },
})
export default class Setting extends Vue {
  showPayment: boolean | string = '';
  showAccountList = false;
  chosenId = '';
  teaParam = 'setting';
  TeaConfig = TeaConfig;
  showFlag = false;
  paymentEl: HTMLElement | null = null;

  @State('notEnterAccountInfo', { namespace }) notEnterAccountInfo: boolean;
  @State('cardInfo', { namespace: 'payment' }) cardInfo: any;
  @State('accountInfo', { namespace }) accountInfo: any;
  @State('coreUserInfo', { namespace }) coreUserInfo: any;
  @State('allAccountList', { namespace }) allAccountList: any;
  @State('payNowABTest') payNowABTest: any;

  @Getter('payment/creditInfo') creditInfo: any;
  @Getter('currency', { namespace }) currency: any;
  @Getter('payment/createUrl') createUrl: string; // 绑定新信用卡URL
  @Getter('payment/isPrepay') isManulPay: boolean;
  @Getter('payment/isPostpay') isAutoPay: boolean;
  @Getter('payment/totalCards') totalCards: number;
  @Getter('payment/isActivePaSDK') isActivePaSDK: boolean;
  @Getter('account/isManagementByBC') isManagementByBC: boolean;
  @Getter('account/isSelfBcAccount') isSelfBcAccount: boolean;
  @Mutation(`payment/${SET_CREDIT_INFO}`) setCreditInfo: any; // fixme
  @Mutation(SET_ENTER_ACCOUNT_INFO, { namespace: 'account' }) setEnterAccountInfo: any; // fixme
  @Mutation('SET_PAYNOW_ABTEST') setPaynowAbtest: any;
  @Action('getChargeInfo', { namespace: 'payment' }) getChargeInfo: any; // fixme
  @Action('account/loadAccountList') loadAccountList: any;
  @Action('account/loadAccountInfo') loadAccountInfo: any;
  @Action('payment/getCardInfo') getCardInfo: () => Promise<{
    identity: string;
  }>;
  @Action('payment/getCardBindLink') getCardBindLink: () => Promise<string>;
  @Action('payment/getPaymentAccount') getPaymentAccount: () => Promise<string>;

  async activated(): Promise<void> {
    try {
      const result = await getAbVersionByName(LibraTestName.PAYNOW_API);
      this.setPaynowAbtest(result.group === LibraTestAbVersionResult.treatment);
      // 获取 credit info
      if (!(result?.group === LibraTestAbVersionResult.treatment)) {
        axios.get(apis.payment.getCreditInfo).then(res => {
          let { data = {} } = res || {};
          try {
            this.setCreditInfo(data);
          } catch (e) {
            console.error(e);
          }
        });
      } else {
        const currentDomain = window.location.host;
        const scene = currentDomain === domainTTP ? PAYMENT_SCENE.TTP_MOBILE : PAYMENT_SCENE.DEFAULT;
        Promise.all([
          axios.get(apis.payment.getCreditInfo),
          axios.post(apis.payment.getUnpaidInvoice, { ad_channel: 'TTAM_MOBILE_LITE', ...getRiskInfo(), scene }),
        ]).then(res => {
          let [{ data: creditInfo = {} }, { data: unpaidInfo = {} }] = res || [{}, {}];
          const data = {
            ...creditInfo,
            tax_detail: JSON.parse(unpaidInfo?.invoice_entity?.tax_detail_map || '{}'),
            amount: unpaidInfo?.invoice_entity?.unpaid_amount,
            balance: unpaidInfo?.invoice_entity?.unpaid_amount_without_tax,
            tax_rate_map: JSON.parse(unpaidInfo?.invoice_entity?.tax_rate_map || '{}'),
            status: unpaidInfo?.invoice_entity?.status,
            invoice_id: unpaidInfo?.invoice_entity?.invoice_id_list,
            tax: unpaidInfo?.invoice_entity?.unpaid_total_tax_amount,
            currency: unpaidInfo?.invoice_entity?.currency,
          };
          try {
            this.setCreditInfo(data);
          } catch (e) {
            console.error(e);
          }
        });
      }
      this.loadAccountList();
    } catch (e) {
      console.log(e);
    }
  }

  created(): void {
    this.chosenId = this.accountInfo.id;
    this.ifShowPayment();
    // 获取主卡信息
    this.getCardInfo();
  }

  mounted(): void {
    reportXFmp();
  }

  // 自动支付才展示高亮
  @Watch('showPayment')
  handleShow(val: boolean): void {
    // 在展示账户列表时不展示高亮
    if (!this.isAutoPay || this.showAccountList || !val) {
      return;
    }
    this.$nextTick().then(() => {
      this.paymentEl = document && document.querySelector('#payment_entry');
      if (this.paymentEl) {
        this.$trackEvent(TeaConfigPayment.payment_lite_autopay_guide_tip, {
          payment_sourse_guide_area: 4,
        });
        this.showFlag = true;
      }
    });
  }

  get isNeedBindCard(): boolean {
    return this.isAutoPay && !this.totalCards;
  }

  get isShowManulPayFail(): boolean {
    return this.isNeedBindCard || this.isCurrentPayFailed;
  }
  // 当前账单的状态
  get currentBillStatus(): number {
    return this.creditInfo?.current_bill?.status;
  }
  // 当前账单是否支付失败
  get isCurrentPayFailed(): boolean {
    return this.currentBillStatus === CurrentBillStatus.Fail || Boolean(this.creditInfo?.invoice_id?.length);
  }

  get avatarTxt(): string {
    return this.coreUserInfo?.core_user?.name?.[0];
  }

  get avatarName(): string {
    return this.coreUserInfo?.core_user?.name || '';
  }

  get avatarBg(): string {
    const bgArray = ['#00ADB5', '#6A65D8', '#6195C5', '#AB8997', '#52686A'];
    const { id = '1' } = this.coreUserInfo?.core_user || {};
    const index = String(id)[String(id).length - 1] as any; // 取尾号
    const idx = Math.floor(index * (bgArray.length / 10)); // 转化成0 - 5
    return bgArray[idx];
  }

  get accountStatus(): string {
    return this.$t(getAccountStatusI18n(Number(this.accountInfo.status)));
  }

  get accountStatusColor(): string {
    return getAccountStatusColor(Number(this.accountInfo.status));
  }

  changeAccount(id: string): void {
    this.chosenId = id;
  }

  get needFix(): boolean {
    return this.notEnterAccountInfo && getAccountStatusItem(Number(this.accountInfo.status)).label === 'disable';
  }

  go2AccountList(): void {
    this.showAccountList = true;
  }
  async handleConfirm(): Promise<void> {
    if (this.allAccountList.length <= 1) {
      this.handleCancel();
      return;
    }
    this.$Loading.show();
    this.showAccountList = false;
    // 切换账号需要更新account info
    await this.loadAccountInfo({
      aadvid: this.chosenId,
      force: true,
    });

    window.location.href = `/wap/perf/?aadvid=${this.chosenId}`; // 详见src/utils/authUtil abtestForward方法注释
  }

  handleCancel(): void {
    this.showAccountList = false;
    this.chosenId = this.accountInfo.id;
  }

  // lite 支持先付款和后付款
  ifShowPayment(): Promise<void> {
    try {
      return Promise.all([this.getChargeInfo(), this.getCardBindLink(), this.getPaymentAccount()]).then(() => {
        if (this.isActivePaSDK) {
          this.showPayment = true;
        } else {
          this.showPayment =
            (this.isManulPay || (this.isAutoPay && Boolean(this.createUrl))) &&
            !(this.isManagementByBC || this.isSelfBcAccount);
        }
      });
    } catch (e) {
      console.error(e);
      this.showPayment = false;
    }
  }

  checkGo2Payment(source: string): void {
    if (!canEdit() || isOperatorOfAgent()) {
      this.$toast({
        message: this.$t('common_account_no_authority'),
      });
      return;
    }

    // 自动支付 在未绑定卡的时候，不检测账户状态，允许跳转绑卡页面
    if (ifPaymentDisable(Number(this.accountInfo.status)) && !this.isNeedBindCard) {
      this.$toast(this.$t('module_notice_payment_hint'));
    } else {
      this.go2Payment(source);
    }
  }

  go2Payment(source: string) {
    // pa激活
    if (this.isActivePaSDK) {
      // platform 8 标记 ttam mobile
      const query = qs.stringify({
        _pia_: 1,
        adv_id: this.chosenId,
        platform: 8,
      });
      console.log('query', query, `${location.origin}/wap/pa/credit_purchase?${query}`);
      window.location.href = `${location.origin}/wap/pa/credit_purchase?${query}`;
      return;
    }

    if (this.isManulPay) {
      if (source === 'add') {
        this.$trackEvent(TeaConfigPayment.payment_lite_settings_prepay_add);
      } else {
        this.$trackEvent(TeaConfigPayment.payment_lite_button_click, {
          payment_type: 1,
        });
      }
      this.$router.push({ name: 'purchase', query: this.$route.query });
    } else if (this.isAutoPay) {
      this.$trackEvent(TeaConfigPayment.payment_lite_button_click, {
        payment_type: 2,
      });
      if (this.isNeedBindCard) {
        this.$trackEvent(TeaConfigPayment.payment_lite_autopay_add_card, {
          payment_sourse_bindcard: 2,
        });
        window.location.href = this.createUrl;
      } else {
        this.$router.push({ name: 'credit_purchase', query: this.$route.query });
      }
    }
  }

  go2accountInfo(): void {
    try {
      this.setEnterAccountInfo();
      this.$router.push({ name: 'account-info', query: this.$route.query });
    } catch (e) {
      this.$toast(this.$t('common_retry'));
    }
  }

  logout(): void {
    logout();
  }
}
